import React, { useState, useEffect, useRef } from "react";
import { Spin, Empty, message as antdMessage } from "antd";
import { CloseOutlined, LoadingOutlined } from "@ant-design/icons";
import { Sparkles, Eye, FileText, Brain } from "lucide-react";
import type {
  GroundingArtifact,
  GroundingSourceRecord,
  GroundingRationaleRecord,
} from "@/types/grounding";
import { useSourceDocumentViewer } from "@/utility/useSourceDocumentViewer";

interface ChatGroundingDrawerProps {
  visible: boolean;
  onClose: () => void;
  taskId: string | null;
  target?: { kind: "source" | "rationale"; id: string } | null;
  retrieveAgentGrounding?: (taskId: string) => Promise<GroundingArtifact>;
  retrieveSourceDocument?: (documentId: string) => Promise<any>;
  projectId?: string | null;
  reportTemplateId?: string | null;
}

export const ChatGroundingDrawer: React.FC<ChatGroundingDrawerProps> = ({
  visible,
  onClose,
  taskId,
  target,
  retrieveAgentGrounding,
  retrieveSourceDocument,
  projectId,
  reportTemplateId,
}) => {
  const [activeTab, setActiveTab] = useState<"sources" | "rationales">("sources");
  const [isLoading, setIsLoading] = useState(false);
  const [artifact, setArtifact] = useState<GroundingArtifact | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [openingDocId, setOpeningDocId] = useState<string | null>(null);

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const { openSourceDocument } = useSourceDocumentViewer();

  // Switch tab automatically based on target reference
  useEffect(() => {
    if (target?.kind === "rationale") {
      setActiveTab("rationales");
    } else if (target?.kind === "source") {
      setActiveTab("sources");
    }
  }, [target]);

  // Fetch grounding artifact when taskId changes or drawer becomes visible
  useEffect(() => {
    if (!visible || !taskId || !retrieveAgentGrounding) {
      if (!visible) {
        setArtifact(null);
        setFetchError(null);
      }
      return undefined;
    }

    let cancelled = false;
    setIsLoading(true);
    setFetchError(null);

    retrieveAgentGrounding(taskId)
      .then((data) => {
        if (cancelled) return;
        setArtifact(data || null);
        setIsLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setFetchError(err?.message || "Failed to load grounding evidence");
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [visible, taskId, retrieveAgentGrounding]);

  // Scroll to targeted source or rationale when loaded
  useEffect(() => {
    if (!visible || !target || !artifact) return undefined;

    const timer = setTimeout(() => {
      const elementId = `grounding-item-${target.id}`;
      const el = document.getElementById(elementId);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 120);

    return () => clearTimeout(timer);
  }, [visible, target, artifact, activeTab]);

  const handleViewDocument = async (source: GroundingSourceRecord) => {
    if (!source.document_id) {
      antdMessage.info("This source has no linked document file.");
      return;
    }
    if (!retrieveSourceDocument) {
      antdMessage.error("Document retrieval service is not available.");
      return;
    }

    try {
      setOpeningDocId(source.document_id);
      await openSourceDocument({
        source,
        retrieveSourceDocument,
        projectId,
        reportTemplateId,
      });
    } catch (err: any) {
      antdMessage.error(err?.message || "Could not open document viewer");
    } finally {
      setOpeningDocId(null);
    }
  };

  if (!visible) return null;

  const sources = artifact?.sources || [];
  const rationales = artifact?.rationales || [];

  return (
    <div className="chat-history-drawer chat-grounding-drawer">
      {/* ── Drawer Header ── */}
      <div className="chat-history-drawer-header">
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Sparkles size={15} style={{ color: "#00b4d8" }} />
          <h3>Grounding Evidence</h3>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="chat-history-drawer-close"
          aria-label="Close Grounding Evidence"
        >
          <CloseOutlined />
        </button>
      </div>

      {/* ── Tabs Bar ── */}
      <div className="chat-grounding-tabs">
        <button
          type="button"
          onClick={() => setActiveTab("sources")}
          className={`chat-grounding-tab-btn ${activeTab === "sources" ? "active" : ""}`}
        >
          <FileText size={13} />
          <span>Sources ({sources.length})</span>
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("rationales")}
          className={`chat-grounding-tab-btn ${activeTab === "rationales" ? "active" : ""}`}
        >
          <Brain size={13} />
          <span>Rationales ({rationales.length})</span>
        </button>
      </div>

      {/* ── Content Area ── */}
      <div className="chat-grounding-content" ref={scrollContainerRef}>
        {isLoading ? (
          <div className="chat-grounding-loading">
            <Spin indicator={<LoadingOutlined spin style={{ fontSize: 24, color: "#00b4d8" }} />} />
            <p>Loading evidence citations…</p>
          </div>
        ) : fetchError ? (
          <div className="chat-grounding-error">
            <p>{fetchError}</p>
          </div>
        ) : activeTab === "sources" ? (
          sources.length === 0 ? (
            <div className="chat-grounding-empty">
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No document sources cited for this response."
              />
            </div>
          ) : (
            <div className="chat-grounding-list">
              {sources.map((source) => {
                const isTargeted = target?.kind === "source" && target?.id === source.id;
                const isOpeningThis = openingDocId === source.document_id;

                return (
                  <div
                    key={source.id}
                    id={`grounding-item-${source.id}`}
                    className={`chat-grounding-card ${isTargeted ? "targeted" : ""}`}
                  >
                    <div className="chat-grounding-card-header">
                      <div className="chat-grounding-badge-group">
                        <span className="chat-grounding-source-badge">{source.id}</span>
                        {source.category && (
                          <span className="chat-grounding-category-badge">{source.category}</span>
                        )}
                      </div>
                      {source.document_id && retrieveSourceDocument && (
                        <button
                          type="button"
                          onClick={() => handleViewDocument(source)}
                          disabled={isOpeningThis}
                          className="chat-grounding-view-doc-btn"
                          title="Open document viewer at this citation"
                        >
                          {isOpeningThis ? (
                            <LoadingOutlined spin style={{ fontSize: 11 }} />
                          ) : (
                            <Eye size={12} />
                          )}
                          <span>View Document</span>
                        </button>
                      )}
                    </div>

                    <div className="chat-grounding-doc-meta">
                      <span className="chat-grounding-doc-name" title={source.source || "Document"}>
                        {source.source || "Document"}
                      </span>
                      {source.locators && (
                        <span className="chat-grounding-locator">
                          {typeof source.locators === "string"
                            ? source.locators
                            : Array.isArray(source.locators)
                            ? source.locators.join(", ")
                            : JSON.stringify(source.locators)}
                        </span>
                      )}
                    </div>

                    {source.excerpt && (
                      <blockquote className="chat-grounding-excerpt">
                        &ldquo;{source.excerpt}&rdquo;
                      </blockquote>
                    )}
                  </div>
                );
              })}
            </div>
          )
        ) : (
          rationales.length === 0 ? (
            <div className="chat-grounding-empty">
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No rationales cited for this response."
              />
            </div>
          ) : (
            <div className="chat-grounding-list">
              {rationales.map((rationale) => {
                const isTargeted = target?.kind === "rationale" && target?.id === rationale.id;

                return (
                  <div
                    key={rationale.id}
                    id={`grounding-item-${rationale.id}`}
                    className={`chat-grounding-card ${isTargeted ? "targeted" : ""}`}
                  >
                    <div className="chat-grounding-card-header">
                      <span className="chat-grounding-rationale-badge">{rationale.id}</span>
                    </div>

                    <div className="chat-grounding-rationale-text">
                      {rationale.rationale || "No rationale description available."}
                    </div>

                    {rationale.source_ids && rationale.source_ids.length > 0 && (
                      <div className="chat-grounding-linked-sources">
                        <span className="chat-grounding-linked-label">Linked Sources:</span>
                        <div className="chat-grounding-chip-list">
                          {rationale.source_ids.map((sId) => (
                            <button
                              key={sId}
                              type="button"
                              onClick={() => {
                                setActiveTab("sources");
                                const timer = setTimeout(() => {
                                  const el = document.getElementById(`grounding-item-${sId}`);
                                  el?.scrollIntoView({ behavior: "smooth", block: "start" });
                                }, 80);
                              }}
                              className="chat-grounding-source-chip"
                              title={`Jump to source ${sId}`}
                            >
                              {sId}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default ChatGroundingDrawer;
